import { useState } from "react";
import { T } from "../utils/theme";
import { formatDate, formatCurrency, ENDPOINTS } from "../utils/api";
import { useFetch, useModal } from "../hooks";
import {
  Card, Spinner, EmptyState, SectionHeader, SearchInput, Modal,
} from "../components/UI";
import { Shield, Eye, Calendar } from "lucide-react";

// ─── Mapper : normalise la réponse API → structure attendue par l'UI ──────────
// L'API retourne : [{ program: { ... }, nbHackers }, ...]
// Le composant attend : [{ titre, recompenseMin, recompenseMax, scope, outOfScope, companyId, statut, ... }]

// Normalise les éléments de scope (peuvent être des objets ou des chaînes)
const normalizeScope = (scopeArray) => {
  if (!Array.isArray(scopeArray)) return [];
  return scopeArray.map(item => {
    if (typeof item === 'string') return item;
    // Si c'est un objet, on extrait target ou description ou on le stringify
    return item?.target || item?.description || item?.type || JSON.stringify(item);
  });
};

const mapProgram = (item) => {
  const p = item?.program ?? item; // compatibilité si l'API change
  return {
    id:           p.id,
    titre:        p.titre,
    description:  p.description,
    statut:       p.statut ?? "actif",
    // Récompenses : on prend prix_bas comme min, prix_critique comme max
    recompenseMin: p.recompenseMin ?? p.prix_bas    ?? 0,
    recompenseMax: p.recompenseMax ?? p.prix_critique ?? 0,
    // Alias scope (normalisé pour gérer objets ou chaînes)
    scope:        normalizeScope(p.scope ?? p.inscope),
    outOfScope:   normalizeScope(p.outOfScope ?? p.outscope),
    // Identifiant entreprise
    companyId:    p.companyId ?? p.company?.id ?? null,
    // Reports et métriques
    reports:      p.reports  ?? [],
    nbHackers:    item.nbHackers ?? p.hackerCount ?? 0,
    createdAt:    p.createdAt,
  };
};

// ─── Couleurs par statut ──────────────────────────────────────────────────────
const statusColor = {
  actif:    { color: T.green,  bg: `${T.green}12`  },
  en_pause: { color: T.yellow, bg: `${T.yellow}12` },
  terminé:  { color: T.muted,  bg: `${T.muted}12`  },
};

// ─── Card ─────────────────────────────────────────────────────────────────────
const ProgramCard = ({ program, onView }) => {
  const sc = statusColor[program.statut] || statusColor.actif;
  return (
    <Card
      className="hov-card"
      style={{ padding: 20, transition: "all .2s", cursor: "pointer" }}
      onClick={() => onView(program)}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0, marginRight: 12,
          background: `${T.blue}18`, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Shield size={18} color={T.blue} />
        </div>
        <span className="badge" style={{ color: sc.color, background: sc.bg }}>
          {program.statut}
        </span>
      </div>

      <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
        {program.titre || "—"}
      </div>
      <div style={{
        fontSize: 11, color: T.muted, marginBottom: 14, lineHeight: 1.5,
        overflow: "hidden", display: "-webkit-box",
        WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
      }}>
        {program.description || "Aucune description"}
      </div>

      {/* Récompenses */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, background: `${T.green}10`, borderRadius: 7, padding: "7px 10px", textAlign: "center" }}>
          <div style={{ fontSize: 9, color: T.muted, marginBottom: 2 }}>MIN</div>
          <div style={{ fontFamily: T.display, fontWeight: 800, fontSize: 13, color: T.green }}>
            {formatCurrency(program.recompenseMin)}
          </div>
        </div>
        <div style={{ flex: 1, background: `${T.blue}10`, borderRadius: 7, padding: "7px 10px", textAlign: "center" }}>
          <div style={{ fontSize: 9, color: T.muted, marginBottom: 2 }}>MAX</div>
          <div style={{ fontFamily: T.display, fontWeight: 800, fontSize: 13, color: T.blue }}>
            {formatCurrency(program.recompenseMax)}
          </div>
        </div>
        {/* Compteur hackers */}
        <div style={{ flex: 1, background: `${T.yellow}10`, borderRadius: 7, padding: "7px 10px", textAlign: "center" }}>
          <div style={{ fontSize: 9, color: T.muted, marginBottom: 2 }}>HACKERS</div>
          <div style={{ fontFamily: T.display, fontWeight: 800, fontSize: 13, color: T.yellow }}>
            {program.nbHackers}
          </div>
        </div>
      </div>

      {/* Scope preview */}
      {program.scope?.length > 0 && (
        <div>
          <div style={{ fontSize: 9, color: T.muted, marginBottom: 5, fontWeight: 700, letterSpacing: ".06em" }}>SCOPE</div>
          <div>
            {program.scope.slice(0, 3).map((s, i) => <span key={i} className="tag">{s}</span>)}
            {program.scope.length > 3 && (
              <span style={{ fontSize: 10, color: T.muted }}> +{program.scope.length - 3}</span>
            )}
          </div>
        </div>
      )}

      {/* Reports badge */}
      {program.reports?.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <span className="badge" style={{ color: T.muted, background: `${T.muted}12`, fontSize: 10 }}>
            {program.reports.length} rapport(s)
          </span>
        </div>
      )}

      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.muted }}>
          <Calendar size={11} />{formatDate(program.createdAt)}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.textSub }}>
          <Eye size={11} /> Voir détails
        </div>
      </div>
    </Card>
  );
};

// ─── Modal détail ─────────────────────────────────────────────────────────────
const ProgramDetailModal = ({ program, open, onClose }) => {
  if (!program) return null;

  // Niveaux de récompense disponibles (selon la structure API réelle)
  const rewardLevels = [
    { label: "Bas",      value: program.prix_bas,      color: T.muted  },
    { label: "Moyen",    value: program.prix_moyen,    color: T.blue   },
    { label: "Élevé",    value: program.prix_eleve,    color: T.yellow },
    { label: "Critique", value: program.prix_critique, color: T.red    },
  ].filter(r => r.value !== undefined);

  return (
    <Modal open={open} onClose={onClose} title="Détails Programme" width={600}>
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <span className="badge" style={{
          color: statusColor[program.statut]?.color || T.green,
          background: statusColor[program.statut]?.bg || `${T.green}12`,
        }}>
          {program.statut}
        </span>
        <span className="badge" style={{ color: T.blue, background: `${T.blue}12` }}>
          {program.nbHackers} hacker(s) inscrit(s)
        </span>
        <span className="badge" style={{ color: T.muted, background: `${T.muted}12` }}>
          {program.reports?.length ?? 0} rapport(s)
        </span>
      </div>

      <h3 style={{ fontFamily: T.display, fontWeight: 800, fontSize: 19, marginBottom: 8 }}>{program.titre}</h3>
      <p style={{ fontSize: 13, color: T.textSub, lineHeight: 1.7, marginBottom: 20 }}>
        {program.description || "Aucune description"}
      </p>

      {/* Grille des récompenses par sévérité */}
      {rewardLevels.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 8 }}>
            RÉCOMPENSES PAR SÉVÉRITÉ
          </div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${rewardLevels.length}, 1fr)`, gap: 8 }}>
            {rewardLevels.map((r, i) => (
              <div key={i} style={{
                background: `${r.color}10`, borderRadius: 8, padding: "8px 10px", textAlign: "center",
                border: `1px solid ${r.color}20`,
              }}>
                <div style={{ fontSize: 9, color: T.muted, marginBottom: 3 }}>{r.label.toUpperCase()}</div>
                <div style={{ fontFamily: T.display, fontWeight: 800, fontSize: 13, color: r.color }}>
                  {formatCurrency(r.value)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid-2" style={{ marginBottom: 18 }}>
        {program.scope?.length > 0 && (
          <div>
            <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 8 }}>SCOPE ✅</div>
            {program.scope.map((s, i) => <span key={i} className="tag">{s}</span>)}
          </div>
        )}
        {program.outOfScope?.length > 0 && (
          <div>
            <div style={{ fontSize: 10, color: T.red, fontWeight: 700, letterSpacing: ".08em", marginBottom: 8 }}>HORS SCOPE ❌</div>
            {program.outOfScope.map((s, i) => (
              <span key={i} className="tag" style={{ background: `${T.red}12`, color: T.red, borderColor: `${T.red}20` }}>{s}</span>
            ))}
          </div>
        )}
      </div>

      <div style={{ background: T.bg, borderRadius: 10, padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: T.muted }}>Créé le</span>
          <span style={{ fontSize: 12, color: T.text }}>{formatDate(program.createdAt)}</span>
        </div>
        <div style={{ height: 1, background: T.border, margin: "10px 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: T.muted }}>Entreprise ID</span>
          <span style={{ fontSize: 12, color: T.blue }}>{program.companyId?.slice(0, 16) ?? "—"}</span>
        </div>
        {program.reports?.length > 0 && (
          <>
            <div style={{ height: 1, background: T.border, margin: "10px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: T.muted }}>Rapports en attente</span>
              <span style={{ fontSize: 12, color: T.yellow }}>
                {program.reports.filter(r => r.statut === "en_attente").length}
              </span>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────
const ProgrammesPage = ({ token }) => {
  // L'API retourne [{ program, nbHackers }] → on mappe au chargement
  const { data: raw, loading } = useFetch(ENDPOINTS.adminPrograms, token);
  const programs = (raw || []).map(mapProgram);

  const modal = useModal();
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = programs.filter(p => {
    if (search && !p.titre?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && p.statut !== statusFilter) return false;
    return true;
  });

  return (
    <div className="fade-up" style={{ padding: "24px 28px", overflowY: "auto", flex: 1 }}>
      <SectionHeader
        title="Programmes Bug Bounty"
        sub={`${programs.length} programme(s) sur la plateforme`}
        action={<SearchInput value={search} onChange={setSearch} style={{ width: 240 }} />}
      />

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 22 }}>
        {[
          { l: "Total",    v: programs.length,                                        c: T.blue   },
          { l: "Actifs",   v: programs.filter(p => p.statut === "actif").length,      c: T.green  },
          { l: "En pause", v: programs.filter(p => p.statut === "en_pause").length,   c: T.yellow },
        ].map((s, i) => (
          <Card key={i} style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: T.muted }}>{s.l}</span>
            <span style={{ fontFamily: T.display, fontWeight: 900, fontSize: 24, color: s.c }}>{s.v}</span>
          </Card>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 4, background: T.surface, borderRadius: 10, padding: 4, width: "fit-content" }}>
          {[
            { id: "all",      label: "Tous" },
            { id: "actif",    label: "Actifs" },
            { id: "en_pause", label: "En pause" },
            { id: "terminé",  label: "Terminés" },
          ].map(tab => (
            <button key={tab.id} onClick={() => setStatusFilter(tab.id)} style={{
              border: "none", borderRadius: 7, padding: "7px 16px",
              fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all .15s",
              background: statusFilter === tab.id ? T.blue : "transparent",
              color: statusFilter === tab.id ? "#fff" : T.muted,
            }}>{tab.label}</button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : filtered.length === 0 ? (
        <EmptyState icon={Shield} title="Aucun programme" sub="Aucun résultat pour les filtres actifs" />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 14 }}>
          {filtered.map((p, i) => (
            <ProgramCard key={p.id || i} program={p} onView={modal.openWith} />
          ))}
        </div>
      )}

      <ProgramDetailModal program={modal.data} open={modal.open} onClose={modal.close} />
    </div>
  );
};

export default ProgrammesPage;